import React, { useState, useEffect } from 'react';
import { T, FONTS } from '../../constants/theme.js';
import { checkSessionCheckInGate } from '../../services/api.js';

/**
 * CheckInGateAlert
 *
 * Renders inside the Actions card in ReservationDetail, just above the
 * "Check In Guest" button. It calls the check-in gate endpoint once
 * when the reservation is confirmed, then:
 *
 *   • Renders nothing for non-session reservations.
 *   • Shows a green banner when the payment threshold is met.
 *   • Shows an amber banner with the outstanding amount when blocked,
 *     and calls onBlock(true) so the parent can disable the button.
 *
 * Props:
 *   reservationId  string              — current reservation ID
 *   onBlock        (blocked: bool) => void
 */
export default function CheckInGateAlert({ reservationId, onBlock }) {
  const [gate,    setGate]    = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reservationId) return;
    let cancelled = false;
    setLoading(true);
    checkSessionCheckInGate(reservationId)
      .then(data => {
        if (cancelled) return;
        setGate(data);
        if (onBlock) onBlock(!data.allowed);
      })
      .catch(() => {
        // Non-fatal — gate not reachable, allow check-in
        if (!cancelled && onBlock) onBlock(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reservationId]); // eslint-disable-line

  // Still fetching or no session link — render nothing, don't block
  if (loading || !gate || !gate.sessionId) return null;

  const paid      = Number(gate.paid      || 0);
  const required  = Number(gate.required  || 0);
  const estimated = Number(gate.estimated || 0);
  const pct       = required > 0 ? Math.min(100, Math.round((paid / required) * 100)) : 100;
  const allowed   = gate.allowed;

  const f = n => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(n));

  return (
    <div style={{
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 6,
      border: `1px solid ${allowed ? T.success + '50' : T.warning + '60'}`,
    }}>
      {/* Coloured header */}
      <div style={{
        background: allowed ? T.success : T.warning,
        padding: '7px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <span style={{ fontSize: 13 }}>{allowed ? '✅' : '⚠️'}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 0.3 }}>
          {allowed ? 'Payment Requirement Met — Check-In Allowed' : 'Payment Required Before Check-In'}
        </span>
      </div>

      {/* Body */}
      <div style={{
        background: allowed ? T.successPale : T.warningPale,
        padding: '10px 12px',
        fontFamily: FONTS.body,
      }}>
        {/* Session info */}
        <div style={{ fontSize: 10, color: T.muted, marginBottom: 8 }}>
          <strong style={{ color: T.navy }}>{gate.companyName}</strong>
          {' · '}{gate.sessionName}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Payment Progress ({gate.requiredPct}% required)
            </span>
            <span style={{ fontSize: 10, fontWeight: 800, color: T.navy }}>{pct}%</span>
          </div>
          <div style={{ height: 5, background: '#D1D5DB', borderRadius: 99 }}>
            <div style={{
              height: 5, borderRadius: 99,
              width: `${pct}%`,
              background: allowed ? T.success : T.warning,
              transition: 'width .3s',
            }} />
          </div>
        </div>

        {/* Amount pills */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: allowed ? 0 : 8 }}>
          {[
            { label: 'Estimated',                    value: f(estimated), color: T.navy    },
            { label: 'Paid',                         value: f(paid),      color: T.success },
            { label: `Min (${gate.requiredPct}%)`,   value: f(required),  color: allowed ? T.success : T.warning },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: '#fff', borderRadius: 7, padding: '5px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>
                {label}
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Block message */}
        {!allowed && (
          <p style={{ fontSize: 10, color: T.warning, lineHeight: 1.5, fontWeight: 600, margin: 0 }}>
            {gate.reason}
          </p>
        )}
      </div>
    </div>
  );
}
